<template>
  <div class="manager-report-tab">
    <!-- Filters Section -->
    <b-row>
      <b-col cols="12">
        <b-card class="filter-card compact">
          <b-card-body>
            <b-row class="align-items-end">
              <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
                <b-form-group label="Manager Name" label-size="sm" class="mb-0">
                  <v-select
                    v-model="managerNameFilter"
                    :options="uniqueManagers"
                    :placeholder="
                      uniqueManagers.length > 0
                        ? 'All managers...'
                        : 'No managers available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueManagers.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
                <b-form-group label="Staff Name" label-size="sm" class="mb-0">
                  <v-select
                    v-model="staffNameFilter"
                    :options="uniqueStaff"
                    :placeholder="
                      uniqueStaff.length > 0
                        ? 'All staff...'
                        : 'No staff available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueStaff.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
                <b-form-group label="Section Name" label-size="sm" class="mb-0">
                  <v-select
                    v-model="sectionNameFilter"
                    :options="uniqueSections"
                    :placeholder="
                      uniqueSections.length > 0
                        ? 'All sections...'
                        : 'No sections available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueSections.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
                <b-form-group label="Status" label-size="sm" class="mb-0">
                  <v-select
                    v-model="statusFilter"
                    :options="uniqueStatuses"
                    :placeholder="
                      uniqueStatuses.length > 0
                        ? 'All statuses...'
                        : 'No statuses available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueStatuses.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col
                xl="3"
                lg="3"
                md="6"
                sm="6"
                cols="12"
                class="mb-1"
                :class="{ 'd-none': !hasActiveFilters }"
              >
                <b-form-group class="mb-0">
                  <b-button
                    @click="clearAllFilters"
                    variant="outline-danger"
                    size="sm"
                    v-b-tooltip.hover
                    title="Clear all filters"
                  >
                    <feather-icon icon="XIcon" size="12" class="mr-1" />
                    Clear All Filters
                  </b-button>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Results Summary -->
    <b-row class="mb-0" v-if="resultData.data.length > 0">
      <b-col cols="12">
        <small class="text-muted">
          Showing {{ filteredResults.length }} manager review(s)
        </small>
      </b-col>
    </b-row>

    <!-- Table Section -->
    <b-row class="mt-1">
      <b-col cols="12" v-if="resultData.data.length > 0">
        <b-overlay :show="tableShow" rounded="true" :fixed="true" no-fade>
          <div
            class="compact-table-container animate__animated animate__fadeIn"
          >
            <!-- No Results Message -->
            <div v-if="filteredResults.length === 0" class="text-center py-5">
              <feather-icon
                icon="SearchIcon"
                size="48"
                class="text-muted mb-3"
              />
              <h5 class="text-muted">No Results Found</h5>
              <p class="text-muted">
                Try adjusting your filters or check if data is available.
              </p>
            </div>

            <!-- Manager Results Table -->
            <div v-else class="results-table-wrapper">
              <div
                class="table-responsive horizontal-scroll"
                style="overflow-x: auto; width: 100%;"
              >
                <b-table
                  :items="paginatedResults"
                  :fields="tableFields"
                  striped
                  hover
                  small
                  class="mb-0 compact-results-table"
                  show-empty
                  empty-text="No manager review results found"
                >
                  <!-- Quality Review ID -->
                  <template #cell(review_id)="data">
                    <span
                      v-if="data.item.review_id"
                      class="font-weight-bold text-primary"
                    >
                      #{{ data.item.review_id }}
                    </span>
                  </template>

                  <!-- Status Badge -->
                  <template #cell(status)="data">
                    <b-badge
                      :variant="getStatusVariant(data.item.status)"
                      class="status-badge"
                    >
                      {{ data.item.status }}
                    </b-badge>
                  </template>

                  <!-- Take Count Badge -->
                  <template #cell(take_count)="data">
                    <b-badge variant="light-primary" class="text-center">
                      {{ data.item.take_count || 0 }}
                    </b-badge>
                  </template>
                </b-table>
              </div>

              <!-- Pagination -->
              <div
                class="d-flex justify-content-between align-items-center mt-3"
              >
                <div>
                  <small class="text-muted">
                    Showing {{ startIndex + 1 }}-{{ endIndex }} of
                    {{ filteredResults.length }} items
                  </small>
                </div>
                <b-pagination
                  v-model="currentPage"
                  :total-rows="filteredResults.length"
                  :per-page="perPage"
                  :hide-goto-end-buttons="false"
                  :prev-text="'Previous'"
                  :next-text="'Next'"
                  :first-text="'First'"
                  :last-text="'Last'"
                  size="sm"
                  class="mb-0"
                />
                <div>
                  <b-form-group class="mb-0 d-flex align-items-center">
                    <label class="mr-2 mb-0 small">Items per page:</label>
                    <b-form-select
                      v-model="perPage"
                      :options="[10, 25, 50, 100]"
                      size="sm"
                      style="width: auto;"
                      @change="currentPage = 1"
                    />
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>
        </b-overlay>
      </b-col>

      <b-col cols="12" v-else>
        <div class="text-center py-5">
          <feather-icon
            icon="AlertCircleIcon"
            size="64"
            class="text-muted mb-3"
          />
          <h3 class="text-muted">No Results Available</h3>
          <p class="text-muted">
            There are no results for this record. Please try a different
            selection.
          </p>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import vSelect from "vue-select";

export default {
  name: "ManagerReportTab",
  components: {
    vSelect,
  },
  props: {
    resultData: {
      type: Object,
      default: () => ({ data: [], total_surveys: 0 }),
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    tableShow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Search filters
      managerNameFilter: "",
      staffNameFilter: "",
      sectionNameFilter: "",
      statusFilter: "",

      // Pagination
      currentPage: 1,
      perPage: 25,

      // Table fields for manager report
      tableFields: [
        {
          key: "review_id",
          label: "Quality Review ID",
          class: "text-center",
          sortable: true,
          thStyle: {
            width: "120px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "120px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "manager_name",
          label: "Manager Name",
          sortable: true,
          thStyle: {
            width: "120px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "120px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "staff_name",
          label: "Staff Name",
          sortable: true,
          thStyle: {
            width: "120px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "120px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "section_name",
          label: "Section Name",
          sortable: true,
          thStyle: {
            width: "140px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "140px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "status",
          label: "Status",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            width: "100px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "100px",
          },
        },
        {
          key: "event_name",
          label: "Event Name",
          sortable: true,
          thStyle: {
            width: "200px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "200px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "take_count",
          label: "Take Count",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            width: "100px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "100px",
          },
        },
      ],
    };
  },
  computed: {
    managerTableData() {
      const managerData = [];

      if (!this.resultData.data || this.resultData.data.length === 0) {
        return [];
      }

      // Track test sequence numbers per staff member
      const staffTestCounts = {};

      // First, sort data by completion time to get chronological order
      const sortedData = [...this.resultData.data].sort((a, b) => {
        const dateA = new Date(a.completion_time || 0);
        const dateB = new Date(b.completion_time || 0);
        return dateA - dateB;
      });

      // Process each group to extract manager-level data
      sortedData.forEach((group, groupIndex) => {
        // Calculate test sequence number for this staff member
        const staffName = group.reviewee_name;
        const testId =
          group.id ||
          group.survey_id ||
          `${group.reviewer_name}_${group.reviewee_name}_${group.event_name}_${group.completion_time}`;

        if (!staffTestCounts[staffName]) {
          staffTestCounts[staffName] = new Set();
        }

        // Only increment if this is a new unique test for this staff member
        if (!staffTestCounts[staffName].has(testId)) {
          staffTestCounts[staffName].add(testId);
        }

        // Get current test number for this staff member
        const currentTestNumber = staffTestCounts[staffName].size;

        // Calculate correct status based on questions
        let calculatedStatus =group.status || "N/A";
        if (group.questions && group.questions.length > 0) {
          // If any question is Failed, the entire group should be Failed
          const hasFailedQuestion = group.questions.some(
            (question) => question.status === "Training needs identified" || question.status === "Training Needs identified" || question.status === "Failed"
          );
          if (hasFailedQuestion) {
            calculatedStatus = "Training Needs identified";
          }
        }

        const managerRow = {
          id: `manager-${groupIndex}`,
          review_id: group.id || group.survey_id || "",
          manager_name: group.reviewer_name || "",
          staff_name: group.reviewee_name || "",
          section_name: group.group_name || "",
          status: calculatedStatus, // Use calculated status instead of group.status
          event_name:
            group.file_name ||
            group.event_name ||
            group.survey_name ||
            "Short Term Advocacy Quality Review",
          take_count: currentTestNumber, // Use sequence number instead of total count
        };

        managerData.push(managerRow);
      });

      return managerData;
    },

    filteredResults() {
      let filtered = this.managerTableData;

      if (this.managerNameFilter) {
        filtered = filtered.filter(
          (item) =>
            item.manager_name &&
            item.manager_name
              .toLowerCase()
              .includes(this.managerNameFilter.toLowerCase())
        );
      }

      if (this.staffNameFilter) {
        filtered = filtered.filter(
          (item) =>
            item.staff_name &&
            item.staff_name
              .toLowerCase()
              .includes(this.staffNameFilter.toLowerCase())
        );
      }

      if (this.sectionNameFilter) {
        filtered = filtered.filter(
          (item) =>
            item.section_name &&
            item.section_name
              .toLowerCase()
              .includes(this.sectionNameFilter.toLowerCase())
        );
      }

      if (this.statusFilter) {
        filtered = filtered.filter((item) => {
          if (!item.status) return false;

          // If user selected "Passed", show all items containing "Passed"
          if (this.statusFilter === "Passed") {
            return item.status.includes("Passed");
          }
          // If user selected "Failed", show all items NOT containing "Passed"
          else if (this.statusFilter === "Failed") {
            return !item.status.includes("Passed");
          }

          return true;
        });
      }

      return filtered;
    },

    paginatedResults() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredResults.slice(start, end);
    },

    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },

    endIndex() {
      const end = this.startIndex + this.perPage;
      return end > this.filteredResults.length
        ? this.filteredResults.length
        : end;
    },

    hasActiveFilters() {
      return !!(
        this.managerNameFilter ||
        this.staffNameFilter ||
        this.sectionNameFilter ||
        this.statusFilter
      );
    },

    uniqueManagers() {
      // Use API-provided filter options for consistency across all tabs
      return this.filterOptions.reviewers || [];
    },

    uniqueStaff() {
      // Use API-provided filter options for consistency across all tabs
      return this.filterOptions.reviewees || [];
    },

    uniqueSections() {
      // Use API-provided filter options for consistency across all tabs
      return this.filterOptions.sections || [];
    },

    uniqueStatuses() {
      // Use API-provided filter options for consistency across all tabs
      // Simplify status options for dropdown
      return this.filterOptions.statuses || [];
  
    },
  },

  methods: {
    onFilterChange() {
      this.currentPage = 1;
      this.$emit("filter-change", {
        managerName: this.managerNameFilter,
        staffName: this.staffNameFilter,
        sectionName: this.sectionNameFilter,
        status: this.statusFilter,
      });
    },

    clearAllFilters() {
      this.managerNameFilter = "";
      this.staffNameFilter = "";
      this.sectionNameFilter = "";
      this.statusFilter = "";
      this.currentPage = 1;
      this.$emit("filter-change", {});
    },

    getStatusVariant(status) {
      // If status contains "Passed" (Group Passed, Passed), show as success
      if (status && (status.includes("Failed") || status.includes("needs") ||  status.includes("identified") || status.includes("Training Needs Identified") || status.includes("Training needs Identified") || status.includes("Training Needs identified"))) {
        return "danger";
      }
      // All other statuses (Compulsory Failed, Failed, etc.) show as danger
      return "success";
    },



    formatDate(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB");
      } catch (error) {
        return dateString;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Import the same styles from the original file
@import "@core/scss/vue/libs/vue-select.scss";

.manager-report-tab {
  // Use the same styling as the original component
  .filter-card {
    border: 1px solid #e3e6f0;
    box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
    border-radius: 0.35rem;

    &.compact {
      .card-body {
        padding: 0.5rem 1rem;
      }
    }

    .form-group {
      margin-bottom: 0.25rem;

      label {
        font-weight: 500;
        color: #5a5c69;
        font-size: 0.75rem;
        margin-bottom: 0.25rem;
      }
    }

    .v-select.form-select-sm {
      .vs__dropdown-toggle {
        border: 1px solid #d1d3e2;
        border-radius: 0.25rem;
        min-height: 32px;

        &:hover {
          border-color: #a0a0a0;
        }

        &.vs--open {
          border-color: #80bdff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      }

      .vs__selected {
        font-size: 0.875rem;
        color: #495057;
      }

      .vs__search {
        font-size: 0.875rem;
      }

      .vs__dropdown-menu {
        font-size: 0.875rem;
        z-index: 9999;
      }
    }
  }

  // Excel-style table headers
  .excel-header {
    background-color: #f8f9fa !important;
    border: 1px solid #dee2e6 !important;
    font-weight: 600 !important;
    font-size: 11px !important;
    padding: 4px 6px !important;
    text-align: center !important;
    color: #495057 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    vertical-align: middle !important;
  }

  .compact-table-container {
    width: 100%;
    overflow-x: auto;

    .results-table-wrapper {
      background: white;
      border-radius: 0.375rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      overflow: hidden;
      width: 100%;

      .table-responsive {
        overflow-x: auto !important;
        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
          height: 8px;
        }

        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;

          &:hover {
            background: #a1a1a1;
          }
        }
      }

      .horizontal-scroll {
        overflow-x: auto !important;
        overflow-y: visible !important;
        white-space: nowrap !important;

        .table {
          min-width: max-content !important;
          white-space: nowrap !important;

          th,
          td {
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
        }
      }
    }

    .table {
      margin-bottom: 0;
      font-size: 0.875rem;

      th,
      td {
        vertical-align: middle;
        border-color: #e3e6f0;
        padding: 0.5rem 0.75rem;
      }

      th {
        background-color: #f8f9fa;
        font-weight: 600;
        border-bottom: 2px solid #e3e6f0;
      }

      .status-badge {
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
      }
    }

    .card-body {
      padding: 1rem 0.75rem;
    }
  }

  .compact-results-table {
    font-size: 0.875rem;
    border-collapse: collapse;
    border: 1px solid #dee2e6;
    table-layout: auto; // Allow dynamic column widths
    width: 100%;
    min-width: max-content;

    thead th {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 2px solid #dee2e6;
      border-right: 1px solid #dee2e6;
      font-weight: 600;
      font-size: 0.8rem;
      padding: 0.75rem 0.5rem;
      vertical-align: middle;
      min-width: 100px !important;
      max-width: 400px !important;
      width: auto !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      white-space: normal !important;
      word-break: normal !important;
    }

    tbody td {
      padding: 0.6rem 0.5rem;
      vertical-align: middle;
      border-top: 1px solid #dee2e6;
      border-right: 1px solid #dee2e6;
      border-bottom: 1px solid #f8f9fa;
      min-width: 100px !important;
      max-width: 400px !important;
      width: auto !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      white-space: normal !important;
      word-break: normal !important;
    }

    tbody tr:hover {
      background-color: rgba(0, 123, 255, 0.04);
    }

    tbody tr:nth-child(even) {
      background-color: #f8f9fa;
    }
  }
}

// Disabled filter styling
.v-select.vs--disabled {
  opacity: 0.6;

  .vs__dropdown-toggle {
    background-color: #f8f9fa !important;
    cursor: not-allowed;
  }

  .vs__selected-options {
    color: #6c757d !important;
  }

  .vs__search {
    cursor: not-allowed;
  }
}
</style>
